// @mui
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
// routes
import { RouterLink } from 'src/routes/components';
import { usePathname } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
// services
// _mock
import { _socials } from 'src/_mock';
// components
import Iconify from 'src/components/iconify';
import Logo from 'src/components/logo';
import { whatsappLink } from 'src/config-global';
import newsletterService from 'src/services/newsletter';

// ----------------------------------------------------------------------

const LINKS = [
  {
    headline: 'Links',
    children: [
      {
        name: 'About Us',
        href: paths.about,
        src: null,
      },
      {
        name: 'Blog',
        href: '/blog',
        src: null,
      },
      {
        name: 'Privacy Policy',
        href: '/privacy-policy',
        target: '_blank',
        src: null,
      },
      {
        name: 'Refund Policy',
        href: paths.refundPolicy,
        target: '_blank',
        src: null,
      },
      {
        name: 'Terms of Service',
        href: '/terms-and-conditions',
        target: '_blank',
        src: null,
      },
    ],
  },
  // {
  //   headline: 'Legal',
  //   children: [
  //     // { name: 'Terms and Condition', href: '#' },
  //   ],
  // },
  {
    headline: 'Contact',
    children: [
      {
        name: 'info@tryvisa.com',
        href: 'mailto:info@tryvisa.com',
        src: '/assets/images/footer/plane.svg',
      },
      {
        name: 'Chat support',
        href: whatsappLink,
        src: '/assets/images/footer/whatsapp.svg',
      },
    ],
  },
];

// ----------------------------------------------------------------------

const isValidEmail = (email) => {
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return emailRegex.test(email);
};

export default function Footer() {
  const pathname = usePathname();
  const isHome = pathname === '/';
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async () => {
    if (!email) {
      enqueueSnackbar('Please enter your email', { variant: 'warning' });
      return;
    }

    if (!isValidEmail(email)) {
      enqueueSnackbar('Please enter a valid email address', { variant: 'warning' });
      return;
    }

    try {
      setIsSubmitting(true);
      await newsletterService.subscribe(email);
      enqueueSnackbar('Successfully subscribed to newsletter!', { variant: 'success' });
      setEmail('');
    } catch (error) {
      console.log('error', error);
      enqueueSnackbar(error || 'Failed to subscribe', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const simpleFooter = (
    <Box
      component="footer"
      sx={{
        py: 5,
        textAlign: 'center',
        position: 'relative',
        bgcolor: 'background.default',
      }}
    >
      <Container>
        <Logo sx={{ mb: 1, mx: 'auto' }} square="true" />

        <Typography variant="caption" component="div">
          © All rights reserved
          <br /> made by
          <Link href="https://tryvisa.com/"> tryvisa </Link>
        </Typography>
      </Container>
    </Box>
  );

  const mainFooter = (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        bgcolor: (theme) => theme.palette.background.footer || 'background.default',
      }}
    >
      <Divider />

      <Container sx={{ pt: 10, pb: 5, overflow: 'hidden' }}>
        {/* Newsletter */}
        <Stack
          spacing={2}
          textAlign="left"
          mb={{ xs: 2, md: 5 }}
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'left', md: 'center' }}
          justifyContent="space-between"
        >
          <Typography color="common.white" variant="h4">
            Join our newsletter to <br />
            keep up to date with us!
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ width: '100%', maxWidth: 480 }}
          >
            <Box
              sx={{
                position: 'relative',
                flexGrow: 1,
              }}
            >
              <Iconify
                icon="mynaui:user"
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  width: 20,
                  height: 20,
                  zIndex: 1,
                }}
              />
              <Box
                component="input"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  px: 3.5,
                  py: 2,
                  pl: 5,
                  color: 'common.white',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'transparent',
                  fontSize: '14px',
                  borderRadius: 4,
                  flexGrow: 1,
                  width: '100%',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1,
                  },
                  '&:focus': {
                    outline: 'none',
                  },
                }}
              />
            </Box>
            <Box
              component="button"
              onClick={handleSubscribe}
              disabled={isSubmitting}
              sx={{
                px: 3.5,
                py: 2,
                bgcolor: 'primary.main',
                color: 'common.white',
                borderRadius: 4,
                border: '0px',
                cursor: 'pointer',
                fontWeight: 'bold',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  cursor: 'not-allowed',
                },
              }}
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ my: { xs: 3, md: 5 } }} />

        <Grid container spacing={{ xs: 2, md: 5 }}>
          {/* Logo & Tagline */}
          <Grid xs={12} md={4}>
            <Logo sx={{ mb: 2, width: { xs: 132, md: 150 }, height: 70 }} />
            <Typography color="common.white" variant="h4">
              Made in Bharat, for the World <br />
              Best Visa Booking Platform Ever
            </Typography>
            {/* <Typography color="common.white" variant="caption">
              tryvisa, 2025
            </Typography> */}
          </Grid>

          {/* Links */}
          <Grid xs={6} md={2}>
            <Typography color="common.white" variant="overline">
              Links
            </Typography>
            <Stack spacing={1} mt={1}>
              {LINKS[0].children.map((link) => (
                <Link
                  sx={{ color: (theme) => alpha(theme.palette.common.white, 0.8) }}
                  key={link.name}
                  component={RouterLink}
                  href={link.href}
                  target={link.target}
                  variant="body2"
                >
                  {link.name}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Contact Info */}
          <Grid xs={12} md={2.5}>
            <Typography
              color="common.white"
              variant="overline"
              sx={{
                marginBottom: '16px', // Use explicit pixel value instead of theme spacing
                display: 'block', // Ensure it's a block element
              }}
            >
              Get Connected
            </Typography>
            <Stack spacing={2}>
              <Typography
                sx={{ color: (theme) => alpha(theme.palette.common.white, 0.8) }}
                variant="body2"
              >
                Ramprasad Mena Technologies Pvt Ltd
              </Typography>
              <Typography
                sx={{ color: (theme) => alpha(theme.palette.common.white, 0.8) }}
                variant="body2"
              >
                10011, EMAAR THE PALM SQUARE, Golf Course Ext Road, Badshahpur, Sector 66, Gurugram,
                Haryana, India - 122102
              </Typography>
              <Link
                sx={{ color: (theme) => alpha(theme.palette.common.white, 0.8) }}
                href="mailto:info@tryvisa.com"
                variant="body2"
              >
                info@tryvisa.com
              </Link>
            </Stack>
          </Grid>

          {/* Twitter Card */}
          <Grid xs={12} md={3.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.10);',
                border: '0.5px solid #ffffff',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    component="img"
                    src="/logo/icon_circle.svg"
                    sx={{ width: 40, height: 40, cursor: 'pointer' }}
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography color="common.white" variant="subtitle2">
                      tryvisa
                    </Typography>
                    <Typography variant="caption" color="common.white">
                      @letstryvisa
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="large" href="https://x.com/letstryvisa" target="_blank">
                  <Iconify icon="ph:x-logo-fill" color="common.white" />
                </IconButton>
              </Stack>
              <Typography variant="body2" color="common.white">
                Were just announced new feature that would help you increase your experience of
                using tryvisa
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: { xs: 3, md: 5 } }} />

        {/* Bottom Bar */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'left', md: 'center' }}
          spacing={2}
        >
          <Typography color="common.white" variant="body2">
            {/* <Logo sx={{ verticalAlign: 'middle', mr: 1, width: 100, height: 24 }} /> */}
            tryvisa, 2025 | All Rights Reserved
          </Typography>

          <Stack direction="row" spacing={1}>
            {_socials.map((social) => (
              <IconButton
                key={social.name}
                href={social.path}
                target="_blank"
                sx={{
                  border: '1px solid rgba(255, 255, 255, 0.24)',
                  bgcolor: alpha(social.color, 0.08),
                }}
              >
                <Iconify icon={social.icon} color={social.color} />
              </IconButton>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );

  // return isHome ? simpleFooter : mainFooter;
  return mainFooter;
}
